import { useEffect, useState } from 'react';
import Head from 'next/head';
import Script from 'next/script';

import { useSnackAdEligibility } from '@hooks/useSnackAdEligibility';
import { useSnackAdSpace } from '@hooks/useSnackAdSpace';
import { useSnackStickyFloorSpace } from '@hooks/useSnackStickyFloorSpace';
import {
  ADLIGHTNING_SCRIPT_ID,
  ADLIGHTNING_SCRIPT_URL,
  FLOOR_HIDDEN_ATTRIBUTE,
  PUBSTACK_LOADER_SCRIPT_ID,
  PUBSTACK_LOADER_SCRIPT_URL,
  SNACK_SITE_NAME,
  getAdSpaceChange,
  isAdFreePath,
  isConfirmedPaidMember,
  shouldLoadSnackAds
} from '@lib/ads/snack';

interface SnackAdLoaderProperties {
  isCookieYesLoaded: boolean;
  pathname: string;
}

/**
 * Loads Snack Media's ad wrapper for visitors who should see ads (FFH-3480),
 * except on the sign-up and checkout pages.
 *
 * Snack need CookieYes, then AdLightning, then their Pubstack loader, in that
 * order. Async scripts run in whatever order they finish downloading, so each
 * one is only added once the previous one has loaded.
 *
 * It also keeps the ad slots' space in step with who the visitor turns out to
 * be (FFH-3481).
 */
const SnackAdLoader = ({
  isCookieYesLoaded,
  pathname
}: SnackAdLoaderProperties) => {
  const eligibility = useSnackAdEligibility();
  const shouldLoadAds = shouldLoadSnackAds(eligibility);
  const isAdFreePage = isAdFreePath(pathname);
  const [hasAdLightningSettled, setHasAdLightningSettled] = useState(false);
  const [hasPubstackFailed, setHasPubstackFailed] = useState(false);
  const isLoadingAds = isCookieYesLoaded && shouldLoadAds && !isAdFreePage;
  const [hasLoadedAds, setHasLoadedAds] = useState(false);

  useEffect(() => {
    if (isLoadingAds) setHasLoadedAds(true);
  }, [isLoadingAds]);

  useEffect(() => {
    if (!isAdFreePage) return;

    const root = document.documentElement;
    root.setAttribute(FLOOR_HIDDEN_ATTRIBUTE, '');
    return () => root.removeAttribute(FLOOR_HIDDEN_ATTRIBUTE);
  }, [isAdFreePage]);

  // Once the wrapper has run, its floor ad stays until the page reloads, even
  // if the visitor stops qualifying (a member upgrading mid-visit, or a failed
  // session check), so keep making room for it.
  useSnackStickyFloorSpace(isLoadingAds || hasLoadedAds);

  // An ad blocker stopping the Pubstack loader means nothing will ever fill
  // the slots, so close their space rather than leave gaps.
  useSnackAdSpace(
    getAdSpaceChange(eligibility, hasPubstackFailed),
    isConfirmedPaidMember(eligibility)
  );

  if (!isLoadingAds) return null;

  // Snack's own snippet loads op.js as a plain script followed by the async
  // loader, so the loader still runs when op.js fails. Settling on error as
  // well as load keeps that behaviour.
  const settleAdLightning = () => setHasAdLightningSettled(true);

  return (
    <>
      <Head>
        <meta
          key="pbstck-context-site-name"
          name="pbstck_context:site_name"
          content={SNACK_SITE_NAME}
        />
        <meta
          key="pbstck-context-website"
          name="pbstck_context:website"
          content={SNACK_SITE_NAME}
        />
      </Head>
      <Script
        id={ADLIGHTNING_SCRIPT_ID}
        src={ADLIGHTNING_SCRIPT_URL}
        strategy="afterInteractive"
        onLoad={settleAdLightning}
        onError={settleAdLightning}
      />
      {hasAdLightningSettled && (
        <Script
          id={PUBSTACK_LOADER_SCRIPT_ID}
          src={PUBSTACK_LOADER_SCRIPT_URL}
          strategy="afterInteractive"
          onError={() => setHasPubstackFailed(true)}
        />
      )}
    </>
  );
};

export default SnackAdLoader;
